'use client';

import { useEffect, useId, useMemo, useState } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { PLANNER_HREF } from '../brand';
import { useDoorCatalogue } from '../data/useDoorCatalogue';
import { SvgArt } from '../drawing/SvgArt';
import { DOOR_BUILDER_CSS } from '../doors/doorBuilderCss';
import { NumberField } from '../doors/NumberField';
import { rangesFor } from '../doors/orderState';
import { roomPlanSvg, shapeIconSvg } from '../planner/plan';
import {
  briefIssues,
  defaultBrief,
  HEIGHT_LIMITS,
  MUST_HAVES,
  SHAPES,
  startHref,
  WALL_LIMITS,
  WISHES_MAX,
  type RoomShape,
  type StartBrief,
} from '../planner/startBrief';
import { Icon } from '../shared/Icon';
import { headingLevel, text, type Localized } from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * The kitchen planner's front door (plan K1, site mockup `planner.html`): the
 * room's shape and sizes, a door range and colour, the must-haves and, if the
 * shopper likes, their wishes in their own words. "Start designing" opens the
 * planner with all of it in a `?start=` brief (`planner/startBrief.ts`).
 *
 * <p>The mockup priced the kitchen here; this does not. Only the planner knows
 * the real layout, so a figure on this page would be a guess presented as a
 * price. The sketch shows the brief was understood, nothing more.
 *
 * <p>The link is a plain anchor, never client-side navigation (the storefront
 * does not own the planner route), and it is rendered on the server with the
 * default brief, so it works before the page's script has loaded.
 *
 * <p><b>Until K2 is built</b> the planner ignores `?start=` and opens a new,
 * empty design. That is the planner's change to make, reported separately.
 */
export interface SpatialKitchenPlannerStartProps {
  title?: Localized;
  intro?: Localized;
  ctaLabel?: Localized;
  /** The planner's address; defaults to the theme's planner route. */
  plannerHref?: string;
  myDesignsHref?: string;
  /** Door range to start on. */
  rangeId?: string;
  shape?: RoomShape;
  headingLevel?: 'h1' | 'h2';
}

const CSS = `
.sk-ps__grid{display:grid;grid-template-columns:minmax(0,1.15fr) minmax(0,1fr);gap:2rem;align-items:start}
@media (max-width:1100px){.sk-ps__grid{grid-template-columns:1fr}}
.sk-ps__stage{position:sticky;top:136px;border-radius:22px;background:var(--sk-white);border:1px solid var(--sk-border);padding:1rem;overflow:hidden}
@media (max-width:1100px){.sk-ps__stage{position:static}}
.sk-ps__stage .sk-art svg{display:block;width:100%;height:auto;max-height:520px}
.sk-ps__stagebar{display:flex;justify-content:space-between;gap:1rem;align-items:center;padding:.25rem .5rem .75rem;font-weight:700}
.sk-ps__stagebar span{font-weight:500;font-size:.82rem;color:var(--sk-faint)}
.sk-ps__shapes{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:8px}
@media (max-width:520px){.sk-ps__shapes{grid-template-columns:repeat(3,minmax(0,1fr))}}
.sk-ps__shapes button{display:grid;justify-items:center;gap:6px;border:1.5px solid var(--sk-border);background:#fff;border-radius:12px;padding:10px 6px;font:inherit;font-size:.78rem;font-weight:700;cursor:pointer}
.sk-ps__shapes button[aria-pressed="true"]{border-color:var(--sk-sage);background:var(--sk-sage-grey)}
.sk-ps__shapes .sk-art{width:44px;height:44px}
.sk-ps__fields{display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:1rem;margin-top:1rem}
.sk-ps__select{width:100%;min-height:44px;border:1.5px solid var(--sk-control-border);border-radius:9px;padding:0 10px;font:inherit;background:#fff}
.sk-ps__chips{display:flex;flex-wrap:wrap;gap:8px}
.sk-ps__chips button{border:1.5px solid var(--sk-border);background:#fff;border-radius:999px;padding:7px 14px;font:inherit;font-size:.86rem;font-weight:700;cursor:pointer;color:var(--sk-muted)}
.sk-ps__chips button[aria-pressed="true"]{background:var(--sk-sage);border-color:var(--sk-sage);color:#fff}
.sk-ps__wishes{width:100%;min-height:110px;border:1.5px solid var(--sk-control-border);border-radius:12px;padding:10px 12px;font:inherit;resize:vertical}
.sk-ps__count{font-size:.78rem;color:var(--sk-faint);text-align:right}
.sk-ps__go{display:grid;gap:.75rem;margin-top:1rem;padding:1.5rem;border-radius:18px;background:var(--sk-ink);color:#fff}
.sk-ps__go p{margin:0;color:#c9ceca;font-size:.88rem}
.sk-ps__go a.sk-btn,.sk-ps__go button.sk-btn{justify-content:center}
.sk-ps__go a:not(.sk-btn){color:#fff}
.sk-ps__issues{margin:0;padding-left:1.1rem;color:#ffd9cc;font-size:.88rem}
`;

export function SpatialKitchenPlannerStart(props: SpatialKitchenPlannerStartProps) {
  const id = useId();
  const artId = useId();
  const doors = useDoorCatalogue();
  const catalogue = doors.status === 'ready' ? doors.catalogue : undefined;
  const ranges = catalogue ? rangesFor(catalogue, 'kitchen') : [];
  const firstRange = ranges.find((r) => r.id === props.rangeId) ?? ranges[0];
  const [brief, setBrief] = useState<StartBrief>(() => defaultBrief(props.shape && SHAPES[props.shape] ? props.shape : 'l'));
  // The door ranges arrive after the first render; start the Style step on the first one.
  useEffect(() => {
    if (firstRange && !brief.range) setBrief((b) => ({ ...b, range: firstRange.id, colour: firstRange.colours[0] }));
  }, [firstRange, brief.range]);
  const range = ranges.find((r) => r.id === brief.range) ?? firstRange;
  const colour = catalogue?.colours().find((c) => c.id === brief.colour);
  const issues = briefIssues(brief);
  const href = startHref(props.plannerHref || PLANNER_HREF, brief);
  const plan = useMemo(() => roomPlanSvg(brief, colour?.hex ?? '#8a9a82', artId), [brief, colour?.hex, artId]);
  const Heading = headingLevel(props.headingLevel, 'h1');
  const set = (patch: Partial<StartBrief>) => setBrief((b) => ({ ...b, ...patch }));
  const shape = SHAPES[brief.shape];

  return (
    <BlockRoot as="section" className="sk sk-db sk-ps" aria-labelledby={`${id}-title`}>
      <SkBaseStyles />
      <SkStyles id="door-builder" css={DOOR_BUILDER_CSS} />
      <SkStyles id="planner-start" css={CSS} />
      <div className="sk-db__wrap">
        <div className="sk-db__head">
          <div>
            <Heading id={`${id}-title`} className="sk-heading sk-db__title">
              {text(props.title, 'Design your kitchen')}
            </Heading>
            <p className="sk-lead">
              {text(
                props.intro,
                'Tell us the shape of the room and what it needs. The planner lays out every cabinet, door and worktop, prices it line by line, and you can change anything before you order.',
              )}
            </p>
          </div>
        </div>

        <div className="sk-ps__grid">
          <div className="sk-ps__stage">
            <div className="sk-ps__stagebar">
              {`${shape.name} kitchen`}
              <span>A sketch; the planner does the real layout</span>
            </div>
            <SvgArt svg={plan} />
          </div>

          <div>
            <div className="sk-db__panel">
              <div className="sk-db__step">
                <span className="sk-db__n" aria-hidden="true">
                  1
                </span>
                <h2 className="sk-heading">Room shape and size</h2>
              </div>
              <div className="sk-ps__shapes" role="group" aria-label="Room shape">
                {(Object.keys(SHAPES) as RoomShape[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={brief.shape === key}
                    onClick={() => setBrief((b) => ({ ...b, shape: key, walls: [...SHAPES[key].lengths] }))}
                  >
                    <SvgArt svg={shapeIconSvg(key)} />
                    {SHAPES[key].name}
                  </button>
                ))}
              </div>
              <div className="sk-ps__fields">
                {shape.walls.map((name, i) => (
                  <div className="sk-db__field" key={`${brief.shape}-${name}`}>
                    <label className="sk-db__label" htmlFor={`${id}-wall-${i}`}>{`Wall ${name}`}</label>
                    <NumberField
                      id={`${id}-wall-${i}`}
                      value={brief.walls[i] ?? 0}
                      step={10}
                      suffix="mm"
                      onCommit={(value) =>
                        setBrief((b) => {
                          const walls = b.walls.slice();
                          walls[i] = Math.round(value);
                          return { ...b, walls };
                        })
                      }
                    />
                  </div>
                ))}
                <div className="sk-db__field">
                  <label className="sk-db__label" htmlFor={`${id}-height`}>
                    Ceiling height
                  </label>
                  <NumberField id={`${id}-height`} value={brief.height} step={10} suffix="mm" onCommit={(value) => set({ height: Math.round(value) })} />
                </div>
              </div>
              <p className="sk-db__hint">{`Walls ${WALL_LIMITS[0]}–${WALL_LIMITS[1]}mm, ceiling ${HEIGHT_LIMITS[0]}–${HEIGHT_LIMITS[1]}mm. Rough is fine: doors, windows and sockets go in next, in the planner.`}</p>
            </div>

            {range && (
              <div className="sk-db__panel">
                <div className="sk-db__step">
                  <span className="sk-db__n" aria-hidden="true">
                    2
                  </span>
                  <h2 className="sk-heading">Style</h2>
                  <span className="sk-db__aside">You can change it later</span>
                </div>
                <div className="sk-ps__fields" style={{ marginTop: 0 }}>
                  <div className="sk-db__field">
                    <label className="sk-db__label" htmlFor={`${id}-range`}>
                      Door range
                    </label>
                    <select
                      id={`${id}-range`}
                      className="sk-ps__select"
                      value={range.id}
                      onChange={(e) => {
                        const next = ranges.find((r) => r.id === e.target.value);
                        if (next) set({ range: next.id, colour: next.colours.includes(brief.colour ?? '') ? brief.colour : next.colours[0] });
                      }}
                    >
                      {ranges.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sk-db__field">
                    <label className="sk-db__label" htmlFor={`${id}-colour`}>
                      Colour
                    </label>
                    <select id={`${id}-colour`} className="sk-ps__select" value={brief.colour} onChange={(e) => set({ colour: e.target.value })}>
                      {range.colours.map((cid) => (
                        <option key={cid} value={cid}>
                          {catalogue?.colours().find((c) => c.id === cid)?.name ?? cid}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="sk-db__panel">
              <div className="sk-db__step">
                <span className="sk-db__n" aria-hidden="true">
                  {range ? 3 : 2}
                </span>
                <h2 className="sk-heading">Appliances and must-haves</h2>
              </div>
              <div className="sk-ps__chips" role="group" aria-label="Appliances and must-haves">
                {MUST_HAVES.map((item) => {
                  const on = brief.must.includes(item);
                  return (
                    <button key={item} type="button" aria-pressed={on} onClick={() => set({ must: on ? brief.must.filter((m) => m !== item) : [...brief.must, item] })}>
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="sk-db__panel">
              <div className="sk-db__step">
                <span className="sk-db__n" aria-hidden="true">
                  {range ? 4 : 3}
                </span>
                <h2 className="sk-heading">
                  <label htmlFor={`${id}-wishes`}>Your wishes</label>
                </h2>
                <span className="sk-db__aside">Optional</span>
              </div>
              <textarea
                id={`${id}-wishes`}
                className="sk-ps__wishes"
                maxLength={WISHES_MAX}
                value={brief.wishes ?? ''}
                placeholder="For example: a big pan drawer by the hob, the fridge away from the oven, open shelves on the short wall."
                aria-describedby={`${id}-wcount`}
                onChange={(e) => set({ wishes: e.target.value })}
              />
              <div className="sk-ps__count" id={`${id}-wcount`}>{`${(brief.wishes ?? '').length} of ${WISHES_MAX}. With wishes, the planner drafts a first layout from them.`}</div>
            </div>

            <div className="sk-ps__go">
              {issues.length > 0 ? (
                <>
                  <ul className="sk-ps__issues" role="status">
                    {issues.map((issue) => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                  <button type="button" className="sk-btn sk-btn--brass sk-btn--lg" disabled>
                    {text(props.ctaLabel, 'Start designing')}
                  </button>
                </>
              ) : (
                <a href={href} className="sk-btn sk-btn--brass sk-btn--lg">
                  {text(props.ctaLabel, 'Start designing')}
                  <Icon name="arrow-right" size={18} />
                </a>
              )}
              <p>
                No account needed to start.{' '}
                {props.myDesignsHref !== '' && <a href={props.myDesignsHref || '/my-designs'}>Open a saved design</a>}
              </p>
            </div>
          </div>
        </div>
      </div>
    </BlockRoot>
  );
}
